import { useStore, useUpdate } from 'dg-web-shared/lib/Flux';
import * as EnforcementState from '../state/EnforcementState';
import * as MasterDataState from '../state/MasterDataState';
import * as AuthTokenState from '../state/AuthTokenState';
import { Result } from './Result';
import { texts } from '../i18n';
import { EnforcementConfig } from './EnforcementConfig';
import { ButtonContainer, TextButton } from './TextButton';
import { Instruction } from './Instruction';
import { Spinner } from './Spinner';
import { logout } from './UserSlideIn';
import { QrCodeScanner } from './TwintQrCodeScanner';
import { GeolocationState } from '../state/GeolocationState';
import { css } from '@emotion/css';
import { Colors } from './vars';
import { AlertBox } from './Modals';
import { LPR_DISCLAIMER_KEY } from '../LprDisclaimerFlags';
import { useState } from 'react';
import { getDevEnvName } from 'dg-web-shared/common/utils/DevEnvironments.tsx';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';

export function Enforcement() {
    const { storeState } = useStore(store => {
        const location = GeolocationState.get(store).currentLocation;
        const layout = EnforcementState.Layout.get(store);
        return {
            masterData: MasterDataState.get(store, {
                location,
                twintPairing: layout.mode === 'twint-qr-code',
            }),
            layout,
            form: EnforcementState.Form.get(store),
            context: EnforcementState.Context.get(store),
            authToken: AuthTokenState.get(store),
        };
    });
    const update = useUpdate();
    const { language } = useLanguage();
    if (
        storeState.masterData.status === 'pending' &&
        !storeState.masterData.data
    ) {
        return <Spinner />;
    }

    if (!storeState.masterData.data) {
        return (
            <ButtonContainer>
                <TextButton
                    label={texts[language].reload}
                    onClick={() => update(logout)}
                />
            </ButtonContainer>
        );
    } else {
        const activeZone = storeState.masterData.data.zones.find(
            it => storeState.form.zoneId === it.id,
        );
        return (
            <div
                className={css({
                    position: 'relative',
                    height: '100%',
                    overflow: 'hidden',
                })}
            >
                <div
                    className={css({
                        position: 'absolute',
                        top: storeState.layout.mode === 'lpr' ? '25%' : 0, // If you change this percentage release a new app
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: Colors.greyBackground,
                    })}
                >
                    <div
                        className={css({
                            position: 'relative',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            background: 'white',
                        })}
                    >
                        <div
                            className={css({
                                display: 'flex',
                                flexGrow: 1,
                                overflow: 'auto',
                            })}
                        >
                            {storeState.layout.mode === 'twint-qr-code' ? (
                                <QrCodeScanner
                                    selectedZone={storeState.form.zoneId}
                                    zoneName={
                                        activeZone
                                            ? `${activeZone.extZoneCode} ${activeZone.name} | ${activeZone.city}`
                                            : ''
                                    }
                                    language={language}
                                />
                            ) : (
                                <EnforcementData
                                    masterData={storeState.masterData}
                                    layout={storeState.layout}
                                    context={storeState.context}
                                    authToken={storeState.authToken}
                                />
                            )}
                        </div>
                        <EnforcementConfig
                            form={storeState.form}
                            layout={storeState.layout}
                            masterData={storeState.masterData.data}
                            context={storeState.context}
                            update={update}
                            language={language}
                        />
                    </div>
                </div>
                <div id="slidein" />
                <div id="modal" />
            </div>
        );
    }
}

function EnforcementData({
    masterData,
    layout,
    context,
    authToken,
}: {
    masterData: MasterDataState.State;
    layout: EnforcementState.Layout.State;
    context: EnforcementState.Context.State | null;
    authToken: AuthTokenState.State;
}) {
    const localStorageDisclaimerKey = `${LPR_DISCLAIMER_KEY}-${masterData.data?.userName}`;
    const [hasAcceptedLPRDisclaimer, setHasAcceptedLPRDisclaimer] = useState(
        () => localStorage.getItem(localStorageDisclaimerKey) === 'true',
    );

    const { language } = useLanguage();
    const update = useUpdate();

    if (!masterData.data) {
        return null;
    }

    const usernameWithEnv =
        masterData.data && masterData.data.userName + ` ${getDevEnvName()}`;

    function acceptDisclaimer() {
        localStorage.setItem(localStorageDisclaimerKey, 'true');
        setHasAcceptedLPRDisclaimer(true);
    }

    if (!authToken.authToken) {
        return null;
    }

    return (
        <>
            {!hasAcceptedLPRDisclaimer && layout.mode === 'lpr' && (
                <AlertBox
                    confirmColor="blue"
                    confirmLabel={texts[language].accept}
                    onConfirm={acceptDisclaimer}
                >
                    <p>
                        <strong>{texts[language].lprCanFailTitle}</strong>
                    </p>
                    <p>{texts[language].lprCanFailPart1}</p>
                    <p>
                        {texts[language].lprCanFailPart2}{' '}
                        <strong>{texts[language].lprCanFailPart3}</strong>{' '}
                        {texts[language].lprCanFailPart4}
                    </p>
                </AlertBox>
            )}
            {context ? (
                <Result
                    context={context}
                    username={usernameWithEnv}
                    authToken={authToken.authToken}
                    language={language}
                />
            ) : (
                <Instruction
                    layout={layout}
                    update={update}
                    username={usernameWithEnv}
                    authToken={authToken.authToken}
                />
            )}
        </>
    );
}
